import { useState } from 'react'
import { FiX } from 'react-icons/fi'

import {
  numberToBRL,
  sumTotalItemsValue,
  translatePaymentInterval,
  translatePaymentMethods,
} from '~/modules/sales/services'
import { PaymentPlan } from '~/modules/sales/types'
import { getLocalDate } from '~/utils/getLocalDate'

import { DeletePlanButton } from './DeletePlanButton'
import { PlanDetailsInstallments } from './PlanDetailsInstallments'
import { PlanLink } from './PlanLink'

type Props = {
  onClose(): void
  data: PaymentPlan
}

export const PlanDetailsModalContent: React.FC<Props> = ({ onClose, data: initialData }) => {
  const [data, setData] = useState(initialData)

  return (
    <div className='grid gap-4'>
      <div className='flex items-center justify-between mb-4'>
        <h1 className='text-xl'>Detalhes do plano</h1>
        <button
          type='button'
          className='p-2 rounded-full hover:bg-slate-50'
          onClick={onClose}
        >
          <FiX size={26} />
        </button>
      </div>

      <div className='flex flex-col gap-4 overflow-hidden'>
        <div className='flex justify-between items-center'>
          <span className='text-lg font-bold'>{data.name}</span>
          <div className='flex items-center gap-2'>
            <span className='text-lg'>{getLocalDate(data.createdAt)}</span>
            <span
              data-active={data.active}
              className='py-1.5 px-2.5 font-semibold text-gpa-blue-500
                    bg-gpa-blue-50 data-[active=false]:bg-red-50 rounded-md
                    data-[active=false]:text-gpa-red'
            >
              {data.active ? 'Ativo' : 'Cancelado'}
            </span>
          </div>
        </div>

        <div className='flex flex-col gap-2 bg-slate-100 p-3 rounded-md'>
          <p>Número de licenças do plano</p>
          <ul className='list-disc flex gap-6 pl-3'>
            <li>{data.licensesAmount}</li>
          </ul>
          <p>Formas de pagamento:</p>
          <ul className='list-disc flex gap-6 pl-3'>
            {translatePaymentMethods(data.metaData?.paymentMethods).map(
              (m, i) => (
                <li key={`${i}_payment_method`}>{m}</li>
              ),
            )}
          </ul>
        </div>

        <div className='flex flex-col gap-2 bg-slate-100 p-3 rounded-md'>
          <PlanDetailsInstallments plan={data} onUpdate={setData} />
        </div>

        <div className='flex flex-col gap-2'>
          <p>Periodicidade:</p>
          <span>
            {translatePaymentInterval(data.interval, data.intervalCount)}
          </span>
        </div>
      </div>

      <div className='flex flex-col gap-2'>
        <p>Itens do plano</p>
        <div className='flex w-full p-3 rounded-md border border-gpa-blue-50 overflow-x-auto'>
          <table className='table-fixed w-full bg-gpa-blue-50 min-w-[500px]'>
            <thead>
              <tr className='text-left'>
                <th>Nome</th>
                <th>Quantidade</th>
                <th>Preço unitário</th>
                <th>Preço total</th>
              </tr>
            </thead>
            <tbody>
              {data.metaData?.items?.map((i, indx) => (
                <tr
                  key={`${data.id}_item_${indx}`}
                  className='even:bg-slate-100 odd:bg-white'
                >
                  <td>{i.name}</td>
                  <td>{i.quantity}</td>
                  <td>{numberToBRL((i.pricing_scheme?.price ?? i.price ?? i.amount ?? 0) / 100)}</td>
                  <td>
                    {numberToBRL((i.quantity * (i.pricing_scheme?.price ?? i.price ?? i.amount ?? 0)) / 100)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className='mb-2 text-right mt-1'>
          <strong>Total: </strong>
          {numberToBRL(sumTotalItemsValue(data.metaData?.items) / 100)}
        </p>
      </div>

      <h1 className='text-lg font-bold'>Link para compra do plano</h1>
      <div
        className='flex flex-col overflow-hidden p-3 bg-slate-100
        rounded-lg'
      >
        <PlanLink planId={data.id} />
      </div>

      {data.active ? <DeletePlanButton planId={data.id} /> : null}
    </div>
  )
}
